import { useEffect, useMemo, useState } from "react";
import { Plus, Shield, Save, Trash2, ArrowLeft, Loader2, Users, ChevronRight, Lock, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import type { PanelLevel } from "../../config/panelCatalog";
import type { PanelPermission } from "../../api/access";
import { getAccess } from "../../api/access";
import { listRoles, createRole, updateRole, deleteRole, getRoleMembers, type Role, type RoleMember } from "../../api/roles";
import { blankPermissions } from "../../utils/accessPermissions";
import { PermissionGridEditor } from "../../components/PermissionGridEditor";
import { InUseGuardModal, type GuardPerson } from "../../components/InUseGuardModal";
import ConfirmDialog, { type ConfirmState } from "../../components/ConfirmDialog";

type Draft = {
  id: string | null;
  name: string;
  rank: number;
  is_system: boolean;
  default_access_id: string | null;
  permissions: Record<string, PanelPermission>;
};

export default function RolesManagement() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [members, setMembers] = useState<RoleMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  // Set when a delete is refused → drives the centered "in use" popup.
  const [blocked, setBlocked] = useState<{ name: string; people: GuardPerson[] } | null>(null);
  // In-app confirmation dialog (replaces native window.confirm).
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setRoles(await listRoles());
    } catch {
      /* keep current list; silent on load failure */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Load the "who" — employees holding the role being edited.
  useEffect(() => {
    if (!draft?.id) {
      setMembers([]);
      return;
    }
    let alive = true;
    setMembersLoading(true);
    getRoleMembers(draft.id)
      .then((m) => alive && setMembers(m))
      .catch(() => alive && setMembers([]))
      .finally(() => alive && setMembersLoading(false));
    return () => {
      alive = false;
    };
  }, [draft?.id]);

  const openNew = () =>
    setDraft({ id: null, name: "", rank: 100, is_system: false, default_access_id: null, permissions: blankPermissions() });

  const openEdit = async (r: Role) => {
    let perms: Record<string, PanelPermission> = {};
    if (r.default_access_id) {
      try {
        perms = (await getAccess(r.default_access_id)).permissions || {};
      } catch {
        perms = {};
      }
    }
    setDraft({
      id: r.id,
      name: r.name,
      rank: r.rank,
      is_system: r.is_system,
      default_access_id: r.default_access_id,
      permissions: blankPermissions(perms),
    });
  };

  const setVisible = (key: string, visible: boolean) =>
    setDraft((d) => (d ? { ...d, permissions: { ...d.permissions, [key]: { ...d.permissions[key], visible } } } : d));
  const setLevel = (key: string, level: PanelLevel) =>
    setDraft((d) => (d ? { ...d, permissions: { ...d.permissions, [key]: { ...d.permissions[key], level } } } : d));

  const save = async () => {
    if (!draft) return;
    if (!draft.name.trim()) {
      toast.error("Give this role a name");
      return;
    }
    setSaving(true);
    try {
      if (draft.id) {
        await updateRole(draft.id, {
          name: draft.is_system ? undefined : draft.name,
          rank: draft.rank,
          permissions: draft.permissions,
        });
        toast.success("Role updated");
      } else {
        await createRole({ name: draft.name, rank: draft.rank, permissions: draft.permissions });
        toast.success("Role created");
      }
      await load();
      setDraft(null);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Jump to the employee's edit page so the manager can move them onto a
  // different role before deleting this one.
  const goToEmployeeEdit = (employeeId: string) => {
    navigate("/employees", { state: { editEmployeeId: employeeId } });
  };

  const remove = async () => {
    if (!draft?.id) return setDraft(null);
    if (draft.is_system) {
      toast.error("System roles cannot be deleted");
      return;
    }
    // Block deletion while anyone still holds this role — deleting it would
    // strip their permissions. Open the centered popup so the manager can jump
    // to reassigning them first.
    if (members.length > 0) {
      setBlocked({
        name: draft.name,
        people: members.map((m) => ({ id: m.id, name: m.name, sub: m.employeeID || m.email || m.role })),
      });
      return;
    }
    const roleId = draft.id;
    setConfirm({
      title: `Delete role "${draft.name}"?`,
      confirmLabel: "Delete",
      tone: "danger",
      onConfirm: async () => {
        try {
          await deleteRole(roleId);
          toast.success("Role deleted");
          setDraft(null);
          await load();
        } catch (e: any) {
          toast.error(e?.response?.data?.detail || "Delete failed");
        }
      },
    });
  };

  const sorted = useMemo(() => [...roles].sort((a, b) => a.rank - b.rank), [roles]);

  if (draft) {
    return (
      <div className="am-root max-w-5xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <button onClick={() => setDraft(null)} className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50" aria-label="Back to roles">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 leading-tight flex items-center gap-2">
                {draft.id ? "Edit Role" : "New Role"}
                {draft.is_system && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
                    <Lock className="w-3 h-3" /> System
                  </span>
                )}
              </h1>
              <p className="text-xs text-gray-400">Set this role's rank and which panels it can access.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!draft.is_system && draft.id && (
              <button onClick={remove} className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-700 px-3 h-10 rounded-xl hover:bg-red-50">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 h-10 rounded-xl shadow-sm disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {draft.id ? "Save Changes" : "Create Role"}
            </button>
          </div>
        </div>

        <div className="am-card bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Role Name</label>
              <input
                value={draft.name}
                disabled={draft.is_system}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. Auditor"
                className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400 disabled:bg-gray-50 disabled:text-gray-500"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Rank (lower = higher authority)</label>
              <input
                type="number"
                value={draft.rank}
                onChange={(e) => setDraft({ ...draft, rank: Number(e.target.value) })}
                className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-400"
              />
            </div>
          </div>
        </div>

        {/* Assigned people — who holds this role */}
        {draft.id && (
          <div className="am-card bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-5">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-emerald-600" />
              <h3 className="text-sm font-semibold text-gray-800">Assigned People</h3>
              <span className="inline-flex items-center text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
                {members.length}
              </span>
            </div>
            {membersLoading ? (
              <div className="text-sm text-gray-400 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : members.length === 0 ? (
              <p className="text-sm text-gray-400">No one has this role yet.</p>
            ) : (
              <>
                <p className="text-xs text-gray-400 mb-2 -mt-1">Tap someone to change their role.</p>
                <div className="flex flex-wrap gap-2">
                  {members.map((m) => (
                    <button
                      type="button"
                      key={m.id}
                      onClick={() => goToEmployeeEdit(m.id)}
                      className="group/chip flex items-center gap-2 border border-gray-100 rounded-xl pl-1.5 pr-3 py-1.5 bg-gray-50/60 hover:bg-emerald-50 hover:border-emerald-200 transition-colors text-left"
                      title={`Edit ${m.name}'s role`}
                    >
                      <div className="w-7 h-7 rounded-lg bg-emerald-500 text-white flex items-center justify-center text-xs font-bold shrink-0">
                        {m.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-800 leading-tight truncate group-hover/chip:text-emerald-700">
                          {m.name}
                        </div>
                        <div className="text-[11px] text-gray-400 leading-tight truncate">
                          {m.employeeID || m.email || ""}
                        </div>
                      </div>
                      <Pencil className="w-3 h-3 text-gray-300 group-hover/chip:text-emerald-500 shrink-0 ml-0.5" />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <PermissionGridEditor permissions={draft.permissions} setVisible={setVisible} setLevel={setLevel} />

        <InUseGuardModal
          open={!!blocked}
          onClose={() => setBlocked(null)}
          entity="role"
          name={blocked?.name || ""}
          people={blocked?.people || []}
          onEditPerson={(id) => {
            setBlocked(null);
            goToEmployeeEdit(id);
          }}
        />

        {/* In-app confirmation dialog (replaces native window.confirm). */}
        <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
      </div>
    );
  }

  return (
    <div className="am-root max-w-5xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-emerald-50 flex items-center justify-center ring-1 ring-emerald-100">
            <Shield className="w-6 h-6 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">Roles</h1>
            <p className="text-sm text-gray-400">Standard roles ship by default. Add custom roles for your org.</p>
          </div>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm px-4 h-10 rounded-xl shadow-sm">
          <Plus className="w-4 h-4" /> Add Role
        </button>
      </div>

      <div className="am-card bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="hidden sm:grid grid-cols-[1fr_120px_140px_40px] gap-3 px-5 py-2.5 bg-gray-50/60 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          <span>Role</span>
          <span className="text-center">Rank</span>
          <span className="text-right">People</span>
          <span />
        </div>
        {loading ? (
          <div className="p-8 text-sm text-gray-400 flex items-center gap-2 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {sorted.map((r) => (
              <button key={r.id} onClick={() => openEdit(r)} className="am-rowlink w-full text-left grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_120px_140px_40px] items-center gap-3 px-5 py-3.5">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center text-sm font-bold shrink-0">
                    {r.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate flex items-center gap-1.5">
                      {r.name}
                      {r.is_system && <Lock className="w-3 h-3 text-gray-300" />}
                    </div>
                    <div className="text-xs text-gray-400 truncate">{r.role_key}</div>
                  </div>
                </div>
                <div className="hidden sm:block text-center text-sm text-gray-600">{r.rank}</div>
                <div className="hidden sm:flex items-center justify-end">
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full px-2.5 py-1">
                    <Users className="w-3 h-3" /> {r.assigned_count ?? 0}
                  </span>
                </div>
                <div className="flex items-center justify-end text-gray-300">
                  <ChevronRight className="w-4 h-4" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
